import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getCourses, getRecommended, getTrending } from '../../api/marketplace';

// ──────────────────────────────────────────────
// Async Thunks
// ──────────────────────────────────────────────

export const fetchCourses = createAsyncThunk(
  'marketplace/fetchCourses',
  async (params, { rejectWithValue }) => {
    try {
      const res = await getCourses(params);
      const courses = Array.isArray(res.data) ? res.data : (res.data?.courses || (Array.isArray(res) ? res : []));
      if (courses.length < 9) {
        const { mockCoursesResponse } = await import('../../api/mockCourses');
        const mockList = mockCoursesResponse.data.courses;
        const merged = [...courses];
        mockList.forEach((mc) => {
          if (!merged.some((c) => (c.title || c.name || '').toLowerCase() === (mc.title || '').toLowerCase())) {
            merged.push(mc);
          }
        });
        return {
          ...res,
          data: merged,
          mocked: true
        };
      }
      return res;
    } catch (error) {
      console.warn('API error or unauthorized - using mock courses data', error);
      const { mockCoursesResponse } = await import('../../api/mockCourses');
      return mockCoursesResponse;
    }
  }
);

export const fetchRecommended = createAsyncThunk(
  'marketplace/fetchRecommended',
  async (_, { rejectWithValue }) => {
    try {
      return await getRecommended();
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch recommended courses' });
    }
  }
);

export const fetchTrending = createAsyncThunk(
  'marketplace/fetchTrending',
  async (_, { rejectWithValue }) => {
    try {
      return await getTrending();
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch trending courses' });
    }
  }
);

// ──────────────────────────────────────────────
// Slice
// ──────────────────────────────────────────────

const marketplaceSlice = createSlice({
  name: 'marketplace',
  initialState: {
    courses: [],
    pagination: {
      page: 1,
      totalPages: 1,
      total: 0
    },
    recommended: [],
    trending: [],
    loading: {
      courses: false,
      recommended: false,
      trending: false,
    },
    error: {
      courses: null,
      recommended: null,
      trending: null,
    },
  },
  reducers: {
    clearMarketplaceErrors: (state) => {
      state.error = { courses: null, recommended: null, trending: null };
    },
  },
  extraReducers: (builder) => {
    builder
      // ── fetchCourses ──
      .addCase(fetchCourses.pending, (state) => {
        state.loading.courses = true;
        state.error.courses = null;
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.loading.courses = false;
        
        const payload = action.payload;
        if (payload) {
          state.courses = Array.isArray(payload.data) ? payload.data : (payload.data?.courses || (Array.isArray(payload) ? payload : []));
          state.pagination = {
            page: payload.page || 1,
            totalPages: payload.totalPages || 1,
            total: payload.total || state.courses.length
          };
        }
      })
      .addCase(fetchCourses.rejected, (state, action) => {
        state.loading.courses = false;
        state.error.courses = action.payload?.message || 'Failed to fetch courses';
      })
      // ── fetchRecommended ──
      .addCase(fetchRecommended.pending, (state) => {
        state.loading.recommended = true;
        state.error.recommended = null;
      })
      .addCase(fetchRecommended.fulfilled, (state, action) => {
        state.loading.recommended = false;
        state.recommended = action.payload.data?.courses || (Array.isArray(action.payload.data) ? action.payload.data : (Array.isArray(action.payload) ? action.payload : []));
      })
      .addCase(fetchRecommended.rejected, (state, action) => {
        state.loading.recommended = false;
        state.error.recommended = action.payload?.message || 'Failed to fetch recommended courses';
      })
      // ── fetchTrending ──
      .addCase(fetchTrending.pending, (state) => {
        state.loading.trending = true;
        state.error.trending = null;
      })
      .addCase(fetchTrending.fulfilled, (state, action) => {
        state.loading.trending = false;
        state.trending = action.payload.data?.courses || (Array.isArray(action.payload.data) ? action.payload.data : (Array.isArray(action.payload) ? action.payload : []));
      })
      .addCase(fetchTrending.rejected, (state, action) => {
        state.loading.trending = false;
        state.error.trending = action.payload?.message || 'Failed to fetch trending courses';
      });
  },
});

export const { clearMarketplaceErrors } = marketplaceSlice.actions;

export default marketplaceSlice.reducer;
